import React from "react";
import { FileText, Paperclip, XCircle } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";

export function SupplierFormFields({ formData, setFormData, isSpanish }) {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">{isSpanish ? "Nombre" : "Name"}</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="valhalla-input"
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">{isSpanish ? "Contacto" : "Contact"}</Label>
          <Input
            value={formData.contact_name}
            onChange={(e) =>
              setFormData({ ...formData, contact_name: e.target.value })
            }
            className="valhalla-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="valhalla-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">{isSpanish ? "Telefono" : "Phone"}</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="valhalla-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">{isSpanish ? "Categoria" : "Category"}</Label>
          <Input
            value={formData.category}
            onChange={(e) =>
              setFormData({ ...formData, category: e.target.value })
            }
            className="valhalla-input"
            placeholder="ej: Software, Servicios..."
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">
            {isSpanish ? "Terminos de pago (dias)" : "Payment terms (days)"}
          </Label>
          <Input
            type="number"
            value={formData.payment_terms}
            onChange={(e) =>
              setFormData({
                ...formData,
                payment_terms: parseInt(e.target.value, 10) || 0,
              })
            }
            className="valhalla-input"
          />
        </div>
      </div>
    </>
  );
}

export function PurchaseOrderFormFields({ formData, setFormData, suppliers, isSpanish }) {
  return (
    <>
      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">{isSpanish ? "Proveedor" : "Supplier"}</Label>
        <Select
          value={formData.supplier_id}
          onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}
        >
          <SelectTrigger className="valhalla-input">
            <SelectValue placeholder={isSpanish ? "Seleccionar" : "Select"} />
          </SelectTrigger>
          <SelectContent className="bg-[#151B26] border-[#242E42]">
            {suppliers.map((supplier) => (
              <SelectItem key={supplier._id} value={supplier._id}>
                {supplier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">{isSpanish ? "Descripcion" : "Description"}</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="valhalla-input"
          rows={3}
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">Subtotal</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.subtotal}
            onChange={(e) => {
              const subtotal = parseFloat(e.target.value) || 0;
              setFormData({
                ...formData,
                subtotal,
                total: subtotal + formData.tax_amount,
              });
            }}
            className="valhalla-input"
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">ITBMS</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.tax_amount}
            onChange={(e) => {
              const taxAmount = parseFloat(e.target.value) || 0;
              setFormData({
                ...formData,
                tax_amount: taxAmount,
                total: formData.subtotal + taxAmount,
              });
            }}
            className="valhalla-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#9CA3AF]">Total</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.total}
            readOnly
            className="valhalla-input bg-[#0B0E14]/50"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[#9CA3AF]">
          {isSpanish ? "Fecha esperada" : "Expected date"}
        </Label>
        <Input
          type="date"
          value={formData.expected_date}
          onChange={(e) =>
            setFormData({ ...formData, expected_date: e.target.value })
          }
          className="valhalla-input"
        />
      </div>
    </>
  );
}

export function PurchaseOrderInvoiceField({
  inputRef,
  file,
  onFileChange,
  onClearFile,
  onSelectFile,
  formatFileSize,
  isSpanish,
  mode = "attach",
}) {
  const title =
    mode === "select"
      ? isSpanish
        ? "Seleccionar factura"
        : "Select invoice"
      : isSpanish
        ? "Adjuntar factura"
        : "Attach invoice";

  return (
    <div className="space-y-2">
      <Label className="text-[#9CA3AF]">
        {isSpanish ? "Factura del proveedor" : "Supplier invoice"}
      </Label>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={(e) => onFileChange(e.target.files?.[0] || null)}
        className="hidden"
      />
      {file ? (
        <div className="rounded-lg border border-[#2F3A51] bg-[#0F1520] p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-[#E2B05E]/25 bg-[#E2B05E]/10">
              <FileText className="w-5 h-5 text-[#E2B05E]" weight="duotone" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-[#F3F4F6]">{file.name}</p>
              <p className="text-xs text-[#9CA3AF]">{formatFileSize(file.size)}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClearFile}
              className="h-8 w-8 p-0 text-[#9CA3AF] hover:text-red-400"
              title={isSpanish ? "Quitar archivo" : "Remove file"}
            >
              <XCircle className="w-5 h-5" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={onSelectFile}
          className="w-full rounded-lg border border-dashed border-[#2F3A51] bg-[#0F1520] p-4 text-left transition-colors hover:bg-[#151B26]"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-[#2F3A51] bg-[#1E2636]">
              <Paperclip className="w-5 h-5 text-[#E2B05E]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#F3F4F6]">{title}</p>
              <p className="text-xs text-[#9CA3AF]">PDF, PNG, JPEG, WebP - 5 MB max</p>
            </div>
          </div>
        </button>
      )}
    </div>
  );
}
